import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import { withRouter, Link } from 'react-router-dom';
import { Modal as ModalPopup } from 'react-bootstrap';
import { changePassword, verifyBySignIn, logOutMvp } from 'redux/actions/user.action';
import { I18n } from '../../../../helpers/i18n';
import Utils from 'helpers/utils';
import warning from '../../../../static/images/mvp/warning.png';
import { toast } from 'react-toastify';
import {
  FormField,
  FormSection, Input, InputLabel,
} from 'aws-amplify-react/lib/Amplify-UI/Amplify-UI-Components-React';

const ChangePassword = ({ user, verifyBySignIn, changePassword, logOutMvp, history }) => {
  const [passwordVerify, setPasswordVerify] = useState(false);
  const [password, setPassword] = useState('');
  const [password2, setPassword2] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [passwordConfirmError, setPasswordConfirmError] = useState('');
  const [currentPassword, setCurrentPassword] = useState('');

  useEffect(() => {
    resetInputs();
  }, []);

  const resetInputs = () => {
    setPasswordConfirmError('');
    setPasswordError('');
    setPassword('');
    setPassword2('');
    setCurrentPassword('');
  };

  const checkPassword = async () => {
    // Clear previous errors
    setPasswordError('');
    setPasswordConfirmError('');

    if (!passwordVerify) {
      // Validate current password is required
      if (!password || password.trim() === '') {
        setPasswordError(I18n.t('text_field_required_error'));
        return;
      }
      
      // Get email - try multiple sources
      let userEmail = null;
      const userData = localStorage.getItem('ced:user-info');
      
      // First try: decode JWT token from localStorage (most reliable)
      if (userData) {
        try {
          const userStorage = JSON.parse(userData);
          if (userStorage && userStorage.accessToken) {
            // Decode JWT token to get email from payload
            // Note: JWT payload is base64 encoded, not encrypted. This is safe because:
            // 1. Email is not highly sensitive information
            // 2. Server always verifies token signature before accepting requests
            // 3. We only use email to send to API, server validates token independently
            const tokenParts = userStorage.accessToken.split('.');
            if (tokenParts.length === 3) {
              try {
                const payload = JSON.parse(atob(tokenParts[1]));
                // Basic validation: check if token is expired
                // Still use email for API call, server will reject if token invalid
                userEmail = payload.email || payload.username;
              } catch (parseError) {
                // Silently continue to next source
              }
            }
          }
        } catch (e) {
          // Silently continue to next source
        }
      }
      
      // Second try: direct property from localStorage
      if (!userEmail || userEmail.trim() === '') {
        if (userData) {
          try {
            const userStorage = JSON.parse(userData);
            userEmail = userStorage.email || 
                       userStorage.user?.email || 
                       userStorage.payload?.email ||
                       (userStorage.username && userStorage.username.includes('@') ? userStorage.username : null);
          } catch (e) {
            // Silently continue to next source
          }
        }
      }
      
      // Third try: Redux state
      if (!userEmail || userEmail.trim() === '') {
        userEmail = user?.email || user?.user?.email || user?.payload?.email || user?.username;
      }
      
      // Final check - if still no email
      if (!userEmail || userEmail.trim() === '') {
        setPasswordError('Không tìm thấy email. Vui lòng đăng nhập lại.');
        return;
      }
      
      // Ensure email is lowercase and trimmed
      userEmail = userEmail.toLowerCase().trim();
      
      try {
        const result = await verifyBySignIn(userEmail, password);
        
        if (result && result.status === 'OK') {
          setPasswordVerify(true);
          setPasswordError('');
          setCurrentPassword(password); // Store current password for validation
          setPassword('');
        } else {
          // Check if error is about wrong password/credentials
          const errorMessage = result?.message || '';
          if (errorMessage.includes('không hợp lệ') || 
              errorMessage.includes('không đúng') || 
              errorMessage.includes('invalid') || 
              errorMessage.includes('incorrect') ||
              errorMessage.includes('not-ok')) {
            setPasswordError(I18n.t('current_password_incorrect'));
          } else {
            setPasswordError(errorMessage || I18n.t('current_password_incorrect'));
          }
        }
      } catch (error) {
        setPasswordError(I18n.t('current_password_incorrect'));
      }
      return;
    }

    // Validate new password is required
    if (!password || password.trim() === '') {
      setPasswordError(I18n.t('text_field_required_error'));
      return;
    }

    // Validate confirm password is required
    if (!password2 || password2.trim() === '') {
      setPasswordConfirmError(I18n.t('text_field_required_error'));
      return;
    }

    // Validate passwords match
    if (password2 !== password) {
      setPasswordConfirmError(I18n.t('repeat_password_match'));
      return;
    }

    // Validate new password is different from current password
    if (currentPassword && password === currentPassword) {
      setPasswordError(I18n.t('new_password_same_as_old'));
      return;
    }

    // Get email - use same logic as verify password step
    let userEmail = null;
    const userData = localStorage.getItem('ced:user-info');
    
    // First try: decode JWT token from localStorage (most reliable)
    if (userData) {
      try {
        const userStorage = JSON.parse(userData);
        if (userStorage && userStorage.accessToken) {
          // Decode JWT token to get email from payload
          const tokenParts = userStorage.accessToken.split('.');
          if (tokenParts.length === 3) {
            try {
              const payload = JSON.parse(atob(tokenParts[1]));
              // Basic validation: check if token is expired
              // Still use email for API call, server will reject if token invalid
              userEmail = payload.email || payload.username;
            } catch (parseError) {
              // Silently continue to next source
            }
          }
        }
      } catch (e) {
        // Silently continue to next source
      }
    }
    
    // Second try: direct property from localStorage
    if (!userEmail || userEmail.trim() === '') {
      if (userData) {
        try {
          const userStorage = JSON.parse(userData);
          userEmail = userStorage.email || 
                     userStorage.user?.email || 
                     userStorage.payload?.email ||
                     (userStorage.username && userStorage.username.includes('@') ? userStorage.username : null);
        } catch (e) {
          // Silently continue to next source
        }
      }
    }
    
    // Third try: Redux state
    if (!userEmail || userEmail.trim() === '') {
      userEmail = user?.email || user?.user?.email || user?.payload?.email || user?.username;
    }
    
    // Final check
    if (!userEmail || userEmail.trim() === '') {
      toast.error('Email không tồn tại. Vui lòng đăng nhập lại.', {
        position: 'top-center',
        autoClose: 3000,
        hideProgressBar: true,
        closeOnClick: true,
        pauseOnHover: false,
        draggable: false,
        progress: undefined,
        className: 'toast-error-container toast-error-container-after',
        theme: 'colored',
      });
      return;
    }
    
    // Ensure email is lowercase and trimmed
    userEmail = userEmail.toLowerCase().trim();
    
    await changePassword(userEmail, password, password2).then(result => {
      if (result.status === 'OK') {
        toast.success(I18n.t('change_password_success_title'), {
          position: 'top-center',
          autoClose: 3000,
          hideProgressBar: true,
          closeOnClick: true,
          pauseOnHover: false,
          draggable: false,
          progress: undefined,
          theme: 'colored',
          className: 'toast-success-container toast-success-container-after',
        });
        setPasswordVerify(false);
        setPasswordError('');
        setPassword('');
        setPasswordConfirmError('');
        setPassword2('');
        setCurrentPassword('');
        // Redirect to profile page
        window.history.back();
      } else {
        // Show specific error message from backend if available
        const errorMessage = result?.message || I18n.t('something_wrong');
        
        // If error is about password same as old, show it in the password field
        if (errorMessage.includes('khác mật khẩu cũ') || 
            errorMessage.includes('different from your current password') ||
            errorMessage.includes('different from current password')) {
          setPasswordError(errorMessage);
        } else {
          toast.error(errorMessage, {
            position: 'top-center',
            autoClose: 3000,
            hideProgressBar: true,
            closeOnClick: true,
            pauseOnHover: false,
            draggable: false,
            progress: undefined,
            className: 'toast-error-container toast-error-container-after',
            theme: 'colored',
          });
        }
      }
    });
  };

  const handleForgotPassword = async () => {
    localStorage.removeItem('ced:pathBeforeSignIn');
    localStorage.removeItem('ced:user-info');
    sessionStorage.removeItem('ced:previousUrl');
    await logOutMvp(user.accessToken).then(() => {
      Utils.removeCookie('_RFT');
      localStorage.removeItem('ced:user-info');
      sessionStorage.removeItem('ced:previousUrl');
    });
    history.push('/forgot-password');
  };

  return (
    <div className="px-0 change-password">
      <style>
        {`
          #modal-change-password .modal-content {
            background-color: #3E4754;
            border: none;
            border-radius: 6px;
            min-width: 580px;
            padding: 60px !important;
          }
          #modal-change-password .modal-header {
            border-bottom: none;
          }
          #modal-change-password .modal-footer {
            border-top: none;
          }
          #modal-change-password .title {
            color: #61E6BD;
            font-size: 36px !important;
          }

          #modal-change-password .verify,
          #modal-change-password .text-white,
          #modal-change-password label {
            color: #fff !important;
            font-size: 18px !important;
            line-height: 150%;
          }
        `}
      </style>
      <ModalPopup
        size="sm"
        show={true}
        onHide={() => window.history.back()}
        id="modal-change-password"
        style={{ opacity: 1 }}
        aria-labelledby="contained-modal-title-vcenter"
        backdrop="static"
      >
        <FormSection style={{ backgroundColor: 'transparent' }}>
          <div className="d-flex align-items-center justify-content-between mb-4">
            <span className="title">
              {passwordVerify ? I18n.t('change_password_title') : I18n.t('confirm_current_password')}
            </span>
          </div>
          <div className="d-flex flex-column mb-3">
            <span className="verify">{passwordVerify ? I18n.t('change_password_des') : I18n.t('confirm_current_password_des')}</span>
          </div>
          <FormField>
            <InputLabel>
              <span className="text-white">
                {passwordVerify ? I18n.t('new_password') : I18n.t('current_password')} <span className="text-danger">*</span>
              </span>
            </InputLabel>
            <Input
              autoFocus
              placeholder={I18n.t('password_hint')}
              key="password"
              name="password"
              type="password"
              style={{ color: '#fff', backgroundColor: '#1F2937', border: '1px solid #374151', borderRadius: '12px', 
                padding: '8px 16px', fontSize: '16px' }}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              pattern=".*"
              autoComplete="current-password"
              required={false}
            />
          </FormField>
          {passwordError && (
            <div style={{ marginTop: '8px', marginBottom: '12px' }}>
              <span className="error-message">
                <img alt="error" src={warning} style={{ marginTop: '-5px', marginRight: '4px' }} />
                {passwordError}
              </span>
            </div>
          )}
          {!passwordVerify && (
            <div style={{ marginTop: passwordError ? '0px' : '12px', marginBottom: '16px' }}>
              <Link 
                to="/forgot-password"
                onClick={(e) => {
                  e.preventDefault();
                  handleForgotPassword();
                }}
                style={{ 
                  display: 'inline-block',
                  fontSize: '18px', 
                  color: '#fff', 
                  cursor: 'pointer',
                  textDecoration: 'underline'
                }}
              >
                {I18n.t('forgot_password')}
              </Link>
            </div>
          )}
          {passwordVerify ?
            <FormField>
              <InputLabel>
                <span className="text-white">
                  {I18n.t('confirm_new_password')} <span className="text-danger">*</span>
                </span>
              </InputLabel>
              <Input
                autoFocus
                placeholder={I18n.t('change_password_hint_text')}
                key="password2"
                name="password2"
                type="password"
                value={password2}
                onChange={(e) => setPassword2(e.target.value)}
                pattern=".*"
                required={false}
                style={{ color: '#fff', backgroundColor: '#1F2937', border: '1px solid #374151', borderRadius: '12px', 
                  padding: '8px 16px', fontSize: '16px' }}
              />
              {passwordConfirmError && (
                <span className="error-message">
                  <img alt="error" src={warning} style={{ marginTop: '-5px', marginRight: '4px' }} />
                  {passwordConfirmError}
                </span>
              )}
            </FormField>
            : null}
          {(() => {
            const isButtonDisabled = 
              (!passwordVerify && (!password || password.trim() === '')) || 
              (passwordVerify && ((!password || password.trim() === '') || (!password2 || password2.trim() === '')));
            
            return (
              <div className="d-flex gap-3" style={{ gap: '12px' }}>
                <div 
                  onClick={() => {
                    if (passwordVerify) {
                      setPasswordVerify(false);
                      resetInputs();
                    } else {
                      window.history.back();
                    }
                  }} 
                  className="verify-cancel" 
                  style={{ 
                    flex: 1, 
                    backgroundColor: '#292F38', 
                    color: '#61E6BD',
                    border: '1px solid #61E6BD',
                    fontSize: '18px',
                    textAlign: 'center',
                    padding: '16px 32px',
                    borderRadius: '16px',
                    cursor: 'pointer'
                  }}
                >
                  {I18n.t('cancel')}
                </div>
                <div 
                  onClick={isButtonDisabled ? null : checkPassword} 
                  className="verify-submit" 
                  style={{ 
                    flex: 1,
                    backgroundColor: isButtonDisabled ? '#374151' : '#61E6BD',
                    textAlign: 'center',
                    color: isButtonDisabled ? '#9CA3AF' : '#3E4754',
                    fontSize: '18px',
                    padding: '16px 32px',
                    borderRadius: '16px',
                    cursor: isButtonDisabled ? 'not-allowed' : 'pointer',
                    opacity: isButtonDisabled ? 0.6 : 1
                  }}
                >
                  {passwordVerify ? I18n.t('change_password_button_title') : I18n.t('next')}
                </div>
              </div>
            );
          })()}
        </FormSection>
      </ModalPopup>
    </div>
  );
};

const mapStateToProps = ({ user }) => ({
  user,
});

const mapDispatchToProps = {
  verifyBySignIn,
  changePassword,
  logOutMvp,
};

export default connect(mapStateToProps, mapDispatchToProps)(withRouter(ChangePassword));

